import { KatButton, KatModal } from '@amzn/katal-react';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'src/utils';
import { Editor, Element as SlateElement, Transforms } from 'slate';
import { ReactEditor, useSlate } from 'slate-react';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { reset } from 'src/redux/slices/image';
import { AppState } from 'src/redux/store';
import { ElementTypes, ImageElement } from 'src/types/CustomSlateTypes';
import FetchStatus from 'src/types/FetchStatus';
import { KatFileUploadStatus, FilesState, ImageTypes } from 'src/types/ImageUpload';
import ImageUpload from 'src/components/ImageUpload';

export const maxImagesAllowed = 5;
export type ImageUploadModalProps = {
  onClose: () => void
};

const ImageUploadModal = ({ onClose }: ImageUploadModalProps) => {
  const editor = useSlate();
  const { t } = useTranslation();
  const [files, setFiles] = useState<FilesState>({});
  const dispatch = useAppDispatch();
  const imageUploadStatus = useAppSelector(({ image }: AppState) => image.status);

  const filesNames = Object.keys(files);
  const filesLength = filesNames.length;
  const maxFileAttachment = useMemo(() => {
    const [...images] = Editor.nodes(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      },
      match: (n) => !Editor.isEditor(n)
      && SlateElement.isElement(n)
      && n.type === ElementTypes.image,
    });
    return maxImagesAllowed - images.length;
  }, []);

  useEffect(() => () => { dispatch(reset()); }, []);

  const attachButtonDisabled = imageUploadStatus === FetchStatus.loading
    || filesLength === 0 || filesLength > maxFileAttachment
    || filesNames.every((fileName) => files[fileName].status === KatFileUploadStatus.error);

  const filesComplete = filesNames.filter(
    (fileName) => files[fileName].status === KatFileUploadStatus.complete,
  );

  if (maxFileAttachment <= 0) {
    return createPortal(
      <KatModal
        data-testid="upload-images-modal-max"
        title={t('imageUpload_max_title')}
        visible
        onClose={onClose}
      >
        <div className="pb-16">
          {t('imageUpload_max_detail')}
        </div>
        <div slot="footer">
          <KatButton variant="secondary" onClick={onClose}>
            {t('close')}
          </KatButton>
        </div>
      </KatModal>,
      document.querySelector('#seller-forums') as Element,
    );
  }

  const handleAddFiles = (newFileState: FilesState) => {
    setFiles((prevFiles) => ({ ...prevFiles, ...newFileState }));
  };

  const handleRemoveFiles = (newFileState: FilesState) => {
    setFiles(newFileState);
  };

  return createPortal(
    <KatModal
      data-testid="upload-images-modal"
      title={t('imageUpload_attach_title')}
      visible
      onClose={onClose}
      persistent
    >
      <ImageUpload
        maxImagesAllowed={maxFileAttachment}
        onFilesAttached={handleAddFiles}
        onFilesRemoved={handleRemoveFiles}
        imageFormatText={t('imageUpload_attach_detail_format')}
        imageSizeText={t('imageUpload_attach_detail_size') as string}
        imageType={ImageTypes.content}
      />
      <div slot="footer">
        <KatButton
          data-testid="image-upload-modal-close-btn"
          variant="secondary"
          className="ltr:mr-2 rtl:ml-2"
          onClick={onClose}
        >
          {t('cancel')}
        </KatButton>
        <KatButton
          data-testid="attach-image-button"
          disabled={attachButtonDisabled}
          onClick={() => {
            filesNames.forEach((name) => {
              const { url, status } = files[name];
              if (status === KatFileUploadStatus.complete && url) {
                const image: ImageElement = {
                  type: ElementTypes.image,
                  url: url as string,
                  children: [{ text: '' }],
                };
                Transforms.insertNodes(editor, image);
              }
            });
            ReactEditor.deselect(editor);
            onClose();
          }}
        >
          { t('imageUpload_attach_button', { filesCompleteCount: filesComplete.length })}
        </KatButton>
      </div>
    </KatModal>,
    document.querySelector('#seller-forums') as Element,
  );
};

export default ImageUploadModal;
